import {useAppForm} from '@cypro/ui/integrations/form'
import {useTranslate} from '@tolgee/react'
import {Button, ButtonGroup, DialogBody, DialogFooter, DialogHeader, HStack, VStack, Text} from '@chakra-ui/react'
import {dialog} from '@cypro/ui/components'
import {revalidateLogic, useStore} from '@tanstack/react-form'
import {getInstructionNoteSchema, InstructionNote} from '@/modules/patients/schemas'
import {getInstructionNoteDefaultValues, mapPatientProtocolsToSelectItems} from '@/modules/patients/functions'
import {useGetSettings} from '@/modules/settings/api'
import {PatientProtocol, PatientTherapy} from '@cypro/engine/queries'
import {computeCycleDayFromTherapyDate, getFirstCycleStartDate} from '@cypro/engine/utils'
import {parseDate} from '@ark-ui/react/date-picker'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {toaster} from '@cypro/ui/toaster'
import {useEffect, useRef} from 'react'

function onClose() {
  dialog.close('add-instruction-note', true)
}

export function AddInstructionNoteDialog({
  patientProtocols,
  patientId,
  existing,
  duplicate = false,
}: {
  patientProtocols: PatientProtocol[]
  patientId: string
  existing?: PatientTherapy
  duplicate?: boolean
}) {
  const {t} = useTranslate('common')

  const protocolItems = mapPatientProtocolsToSelectItems(patientProtocols)

  const zero = useZero()
  const {settings} = useGetSettings()
  const isSyncingDateWithCycleDayRef = useRef(false)
  const isAnEdit = Boolean(existing?.id) && !duplicate

  const form = useAppForm({
    defaultValues: getInstructionNoteDefaultValues(duplicate, existing),
    validators: {
      onDynamic: getInstructionNoteSchema(patientProtocols),
    },
    validationLogic: revalidateLogic(),
    onSubmit(props) {
      const values = {
        patientId,
        patientProtocolId: props.value.patientProtocolId,
        cycle: props.value.cycle,
        day: props.value.day,
        time: props.value.time,
        isNote: props.value.isNote,
        note: props.value.note,
        recurringCycle: props.value.recurringCycle,
        applyToFutureNotes: props.value.applyToFutureNotes,
        ...(props.value.fixedDate[0] && {
          fixedDate: props.value.fixedDate[0].toDate('UTC').getTime(),
        }),
      }

      dialog.close('add-instruction-note', true)
      if (isAnEdit && existing) {
        return updateInstructionNote({
          id: existing.id,
          ...values,
        })
      } else {
        return addInstructionNote(values)
      }
    },
  })

  function addInstructionNote(payload: Parameters<typeof zero.mutators.patients.addInstructionNote>[0]) {
    return zero.attemptMutation(zero.mutators.patients.addInstructionNote(payload), async mutation => {
      const data = await mutation.server
      if (!data) return

      toaster.success({title: t('success'), description: t('instruction_note_created_successfully')})
      form.reset()
    })
  }

  function updateInstructionNote(payload: Parameters<typeof zero.mutators.patients.updateInstructionNote>[0]) {
    return zero.attemptMutation(zero.mutators.patients.updateInstructionNote(payload), async mutation => {
      const data = await mutation.server
      if (!data) return

      toaster.success({title: t('success'), description: t('instruction_note_updated_successfully')})
      form.reset()
    })
  }

  function onSubmit() {
    form.handleSubmit()
  }

  const protocol = useStore(form.store, state => state.values.patientProtocolId)
  const selectedPatientProtocol = patientProtocols.find(pp => pp.id === protocol[0])
  const dayZeroProtocol = Boolean(settings?.dayZeroProtocol)

  function onFixedDateChange(value: InstructionNote['fixedDate']) {
    const date = value?.[0]
    if (isSyncingDateWithCycleDayRef.current || !date || !selectedPatientProtocol?.protocol) return
    const {cycle, day} = computeCycleDayFromTherapyDate(
      selectedPatientProtocol,
      date.toDate('UTC').getTime(),
      selectedPatientProtocol.protocol.daysRepetition,
      dayZeroProtocol
    )
    isSyncingDateWithCycleDayRef.current = true
    form.setFieldValue('cycle', cycle, {dontRunListeners: true, dontUpdateMeta: true})
    form.setFieldValue('day', day, {dontRunListeners: true, dontUpdateMeta: true})
    isSyncingDateWithCycleDayRef.current = false
  }

  // Cycle/day are read-only and derived from the date, so compute them for a prefilled date
  // (duplicate/edit) and whenever the protocol or day-zero setting they depend on changes.
  useEffect(() => {
    onFixedDateChange(form.getFieldValue('fixedDate'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatientProtocol, dayZeroProtocol])

  return (
    <>
      <DialogHeader>
        <Text fontSize="lg" fontWeight="semibold">
          {t('protocol.add_instruction_note')}
        </Text>
      </DialogHeader>

      <DialogBody minHeight={'none'} maxWidth={'100%'}>
        <VStack gapY="5" overflow={'none'}>
          <form.AppField name="patientProtocolId">
            {field => (
              <field.Select
                label={t('protocols')}
                placeholder={t('select_protocol')}
                items={protocolItems}
                contentProps={{zIndex: 2000}}
                displayClearTrigger
                emptyMessage={t('no_protocols_assigned')}
                // the note can't be moved to another protocol once created
                disabled={isAnEdit}
              />
            )}
          </form.AppField>
          <HStack width={'100%'} alignItems="flex-start">
            <form.AppField name="fixedDate" listeners={{onChange: ({value}) => onFixedDateChange(value)}}>
              {field => (
                <field.DatePicker
                  label={t('date')}
                  required
                  minDate={
                    selectedPatientProtocol
                      ? parseDate(new Date(getFirstCycleStartDate(selectedPatientProtocol)))
                      : undefined
                  }
                />
              )}
            </form.AppField>

            <form.AppField name="time">
              {field => <field.HourInput label={t('therapy_sequence.time')} placeholder="13:45" required />}
            </form.AppField>
          </HStack>
          {selectedPatientProtocol && (
            <>
              <HStack width={'100%'} alignItems="flex-start">
                <form.AppField name="cycle">
                  {field => (
                    <field.NumberInput
                      label={t('protocol.cycle')}
                      placeholder="1"
                      min={1}
                      required
                      disabled
                      errorMessagePadding={24}
                    />
                  )}
                </form.AppField>
                <form.AppField name="day">
                  {field => <field.NumberInput label={t('therapy_sequence.day')} placeholder="0" required disabled />}
                </form.AppField>
              </HStack>
              {isAnEdit ? (
                <form.AppField name="applyToFutureNotes">
                  {field => (
                    <field.CheckBox
                      label={t('instruction_note.apply_to_future_notes.title')}
                      description={t('instruction_note.apply_to_future_notes.description')}
                    />
                  )}
                </form.AppField>
              ) : (
                <form.AppField name="recurringCycle">
                  {field => (
                    <field.CheckBox label={t('recurring_cycle.title')} description={t('recurring_cycle.description')} />
                  )}
                </form.AppField>
              )}
            </>
          )}
          <form.AppField name="note">
            {field => (
              <field.Textarea
                label={t('notes')}
                placeholder={t('protocol.guideline_note')}
                rows={4}
                bgColor="white"
                required
              />
            )}
          </form.AppField>
        </VStack>
      </DialogBody>

      <DialogFooter>
        <ButtonGroup size="md" colorPalette="cyan" width="100%" justifyContent="space-between">
          <Button variant={'ghost'} colorPalette={'cyan'} onClick={() => onClose()}>
            {t('cancel')}
          </Button>
          <Button onClick={() => onSubmit()}>{t('protocol.add_parameter')}</Button>
        </ButtonGroup>
      </DialogFooter>
    </>
  )
}
